import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type AdminSettings = components['schemas']['AdminSettings'];

/** The white-label settings in effect, the deployment's defaults and the overrides. */
export function useAdminSettings() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.adminSettings(),
    queryFn: () => unwrap(api.GET('/api/admin/settings')),
  });
}

/** Adds an external ID type, then refreshes the types. 409 when the name is taken. */
export function useCreateExternalIdType() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      unwrap(api.POST('/api/admin/external-id-types', { body: { name } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.externalIdTypes() }),
  });
}

/** Deletes an external ID type, then refreshes the types. 409 while users have one. */
export function useDeleteExternalIdType() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE('/api/admin/external-id-types/{type_id}', {
          params: { path: { type_id: id } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.externalIdTypes() }),
  });
}

/**
 * Switches the optional roles (ADR 0028): `enabledRoles` on, the rest off. Afterwards the
 * settings and the enabled roles (which say what to offer) are refetched.
 */
export function useSaveRoles() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabledRoles: NonNullable<SettingsUpdate['enabledRoles']>) =>
      unwrap(api.PATCH('/api/admin/settings', { body: { enabledRoles } })),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.roles() }),
      ]),
  });
}

/** The pay history page's settings, as saved (A15). */
export interface PayHistorySettings {
  showPaySummary: boolean;
  showCategoryBreakdown: boolean;
}

/**
 * Saves the pay history page's settings (A15). Afterwards the settings and the checks
 * (which carry the effective values) are refetched.
 */
export function useSavePayHistory() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PayHistorySettings) => unwrap(api.PATCH('/api/admin/settings', { body })),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
      ]),
  });
}

export type LineItemTable = components['schemas']['LineItemTable'];
export type LineItemTableUpdate = components['schemas']['LineItemTableUpdate'];

/** The line item table's columns, in order, with their labels (ADR 0030). */
export function useLineItemTable() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.lineItemTable(),
    queryFn: () => unwrap(api.GET('/api/admin/settings/line-item-table')),
  });
}

/**
 * Replaces the line item table's columns. Afterwards the setting and everything under
 * pay are refetched, so tables, Line-item Search and statements follow at once.
 */
export function useSaveLineItemTable() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LineItemTableUpdate) =>
      unwrap(api.PUT('/api/admin/settings/line-item-table', { body })),
    onSuccess: (table) => queryClient.setQueryData(queryKeys.lineItemTable(), table),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.lineItemTable() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
      ]),
  });
}

export type LogoVariant = 'light' | 'dark';
export type SettingsUpdate = components['schemas']['SettingsUpdate'];

/** A logo change on save: a new file, or back to the deployment's (null). */
export type LogoChange = File | null;

export interface WhiteLabelChanges {
  fields: SettingsUpdate;
  logos: Partial<Record<LogoVariant, LogoChange>>;
}

/**
 * Saves the white-label overrides: the logos first (an unsafe SVG is the likeliest
 * refusal, and nothing else has changed yet), then the fields. Afterwards the settings
 * and the public tenant are refetched, so the shell (and Login) re-brand at once.
 */
export function useSaveWhiteLabel() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fields, logos }: WhiteLabelChanges) => {
      for (const variant of ['light', 'dark'] as const) {
        const change = logos[variant];
        if (change === undefined) continue;
        const path = { params: { path: { variant } } };
        if (change === null) {
          await unwrap(api.DELETE('/api/admin/settings/logo/{variant}', path));
        } else {
          const body = new FormData();
          body.append('file', change);
          await unwrap(
            api.PUT('/api/admin/settings/logo/{variant}', {
              ...path,
              // A FormData body is sent as is, with its multipart boundary.
              body: body as unknown as { file: string },
            }),
          );
        }
      }
      if (Object.keys(fields).length > 0) {
        await unwrap(api.PATCH('/api/admin/settings', { body: fields }));
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tenant() }),
      ]),
  });
}
